import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Eye, EyeOff, Radio, ExternalLink, Info } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'sante', label: 'Santé' },
  { id: 'environnement', label: 'Environnement / Nature' },
  { id: 'social', label: 'Protection sociale' },
  { id: 'emploi', label: 'Offres d\'emploi RDC' },
  { id: 'actualite', label: 'Actualité générale' },
];

const EMPTY = {
  title: '', source_name: '', source_url: '',
  category: 'actualite', summary: '', active: true
};

export default function NewsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/news`, { withCredentials: true });
      setItems(res.data);
    } catch (e) { console.error('Failed to load news:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.source_url.startsWith('http')) {
      toast.error('Le lien de la source doit commencer par http(s)://');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/admin/news/${editingId}`, data, { withCredentials: true });
        toast.success('Actualité mise à jour');
      } else {
        await axios.post(`${API}/admin/news`, data, { withCredentials: true });
        toast.success('Actualité ajoutée');
      }
      reset();
      fetchAll();
    } catch (err) { toast.error('Erreur'); }
  };

  const reset = () => { setData(EMPTY); setEditingId(null); setShowForm(false); };

  const handleEdit = (n) => {
    setEditingId(n.news_id);
    setData({
      title: n.title, source_name: n.source_name, source_url: n.source_url,
      category: n.category, summary: n.summary || '', active: n.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    try { await axios.delete(`${API}/admin/news/${id}`, { withCredentials: true }); toast.success('Supprimée'); fetchAll(); }
    catch (e) { toast.error('Erreur'); }
  };

  const toggleActive = async (n) => {
    try {
      await axios.put(`${API}/admin/news/${n.news_id}`, { ...n, active: !n.active }, { withCredentials: true });
      toast.success(n.active ? 'Désactivée' : 'Activée');
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
  };

  return (
    <div data-testid="news-manager">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-[#2E332D] inline-flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          <Radio className="text-[#D87D4A]" size={22} />
          Bande-annonce d&apos;actualités ({items.length})
        </h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary inline-flex items-center" data-testid="add-news-button">
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter une actualité'}
        </button>
      </div>

      <div className="bg-orange-50 border-l-4 border-orange-400 rounded p-3 mb-4 flex gap-2 text-sm text-orange-900">
        <Info size={16} className="shrink-0 mt-0.5" />
        <span>
          <strong>Note de modération :</strong> les actualités diffusées doivent être préalablement vérifiées et censurées. CRESA partage uniquement les contenus
          qui n&apos;engagent pas son ASBL — santé, environnement / nature, protection sociale et offres d&apos;emploi RDC.
          Chaque actualité doit citer sa source.
        </span>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6" data-testid="news-form">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Titre de l&apos;actualité *</label>
              <input required value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="news-title-input"
                placeholder="Ex: L'OMS publie un nouveau rapport sur..." />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Nom de la source *</label>
                <input required value={data.source_name} onChange={(e) => setData({ ...data, source_name: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="news-source-name-input"
                  placeholder="Ex: Radio Okapi, OMS, Reuters" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#2E332D] mb-1">Catégorie *</label>
                <select value={data.category} onChange={(e) => setData({ ...data, category: e.target.value })}
                  className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="news-category-select">
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">URL de la source *</label>
              <input type="url" required value={data.source_url} onChange={(e) => setData({ ...data, source_url: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" data-testid="news-url-input"
                placeholder="https://..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Résumé (optionnel)</label>
              <textarea rows={2} value={data.summary} onChange={(e) => setData({ ...data, summary: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="news-active" checked={data.active} onChange={(e) => setData({ ...data, active: e.target.checked })} className="h-4 w-4" />
              <label htmlFor="news-active" className="text-sm">Actif (visible dans la bande-annonce publique)</label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="submit-news-button">{editingId ? 'Mettre à jour' : 'Ajouter'}</button>
              <button type="button" onClick={reset} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]"></div></div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#5C665A] py-8">Aucune actualité. Ajoutez-en pour démarrer la bande-annonce.</p>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.news_id} className="card" data-testid={`admin-news-${n.news_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${n.active ? 'bg-[#4A6B48]/10 text-[#4A6B48]' : 'bg-gray-100 text-gray-500'}`}>
                      {n.active ? 'Actif' : 'Inactif'}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                      {CATEGORIES.find((c) => c.id === n.category)?.label || n.category}
                    </span>
                    <span className="text-xs text-[#5C665A] italic">{n.source_name}</span>
                  </div>
                  <p className="text-sm font-bold text-[#2E332D]">{n.title}</p>
                  {n.summary && <p className="text-xs text-[#5C665A] mt-1">{n.summary}</p>}
                  <a href={n.source_url} target="_blank" rel="noreferrer" className="text-xs text-[#4A6B48] hover:underline inline-flex items-center gap-1 mt-1">
                    <ExternalLink size={10} /> Voir la source
                  </a>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => toggleActive(n)} className="p-2 hover:bg-gray-100 rounded">
                    {n.active ? <EyeOff size={16} className="text-orange-600" /> : <Eye size={16} className="text-[#4A6B48]" />}
                  </button>
                  <button onClick={() => handleEdit(n)} className="p-2 hover:bg-gray-100 rounded"><Edit2 size={16} className="text-[#4A6B48]" /></button>
                  <button onClick={() => handleDelete(n.news_id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 size={16} className="text-red-500" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
